import { ReactNode, memo, useCallback, useMemo } from "react";
import Draggable from "react-draggable";

import MuiDialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent, { DialogContentProps } from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import type { PaperProps } from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import { Breakpoint, SxProps, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { CloseIcon } from "components/icons";

interface DialogComponentProps {
  readonly title?: string;
  readonly subtitle?: ReactNode;
  readonly titleTextSx?: SxProps;
  /** Styles the subtitle line — e.g. a light colour when `dialogTitleSx` paints a dark bar. */
  readonly subtitleTextSx?: SxProps;
  readonly open: boolean;
  readonly children: ReactNode;
  readonly onClose: (event: object, reason: string) => void;
  readonly dialogContentProps?: DialogContentProps;
  readonly minWidth?: number;
  readonly maxWidth?: Breakpoint;
  readonly sx?: SxProps;
  readonly paperContentSx?: SxProps;
  readonly dialogTitleSx?: SxProps;
  readonly isDraggable?: boolean;
  readonly hideBackdrop?: boolean;
  readonly disableEnforceFocus?: boolean;
  readonly disableScrollLock?: boolean;
  /**
   * Only the close button (or an explicit caller action) may close the dialog — a backdrop
   * click and the Escape key are ignored. For dialogs where an accidental dismissal loses
   * work or hides an operation already in flight (e.g. posting a payment).
   */
  readonly disableDismissOnClickAway?: boolean;
  readonly extraTitleButton?: ReactNode;
  /**
   * Footer buttons rendered in a `DialogActions` OUTSIDE the scrolling `DialogContent`,
   * so they stay visible while the body scrolls. A submit button placed here is outside
   * the body's `<form>`; bind it with the `form="<form id>"` attribute.
   */
  readonly actions?: ReactNode;
  readonly dialogActionsSx?: SxProps;
}

function Dialog({
  title,
  subtitle,
  titleTextSx,
  subtitleTextSx,
  open,
  children,
  dialogContentProps,
  paperContentSx,
  dialogTitleSx,
  onClose,
  minWidth = 586,
  maxWidth,
  isDraggable,
  hideBackdrop,
  disableEnforceFocus,
  disableScrollLock,
  disableDismissOnClickAway,
  extraTitleButton,
  actions,
  dialogActionsSx,
  sx
}: DialogComponentProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("mobile"));
  const isTablet = useMediaQuery(theme.breakpoints.down("tablet"));
  const { typography } = useTheme();

  const handleClose = useCallback(
    (event: object, reason: string) => {
      if (
        disableDismissOnClickAway &&
        (reason === "backdropClick" || reason === "escapeKeyDown")
      ) {
        return;
      }
      onClose(event, reason);
    },
    [disableDismissOnClickAway, onClose]
  );

  // Memoize the close button click handler
  const handleCloseClick = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => onClose(e, "closeButton"),
    [onClose]
  );

  // Memoize the DialogTitle sx object to prevent recreation on every render
  const dialogTitleSxMemo = useMemo(
    () =>
      ({
        alignItems: "center",
        backgroundColor: grey[200],
        ...(isDraggable && { cursor: "move" }),
        "&:hover": isDraggable ? { background: grey[300] } : {},
        pointerEvents: "auto",
        ...dialogTitleSx
      }) as SxProps,
    [isDraggable, dialogTitleSx]
  );

  // Memoize the Paper sx object
  const paperSxMemo = useMemo(
    () =>
      ({
        minWidth: isMobile ? "calc(100% - 40px)" : typography.pxToRem(minWidth),
        ...(isMobile && { width: "calc(100% - 40px)" }),
        ...(isTablet && !isMobile && { width: typography.pxToRem(minWidth) }),
        maxWidth: "100%",
        borderRadius: "12px",
        pointerEvents: "auto",
        ...paperContentSx
      }) as any,
    [isMobile, isTablet, typography, minWidth, paperContentSx]
  );

  const PaperComponent = useCallback(
    (props: PaperProps) => {
      return (
        <Draggable
          axis={isDraggable ? undefined : "none"}
          handle=".draggable-dialog-title"
          cancel={'[class*="MuiDialogContent-root"]'}
        >
          <Paper {...props} sx={paperSxMemo} />
        </Draggable>
      );
    },
    [isDraggable, paperSxMemo]
  );

  return (
    <MuiDialog
      sx={sx}
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      PaperComponent={PaperComponent}
      hideBackdrop={hideBackdrop}
      disableEscapeKeyDown={disableDismissOnClickAway}
      disableEnforceFocus={disableEnforceFocus}
      disableScrollLock={disableScrollLock}
    >
      <DialogTitle sx={dialogTitleSxMemo} className="draggable-dialog-title">
        <Stack justifyContent="space-between" direction="row" width="100%">
          {Boolean(title) && (
            <Stack>
              <Typography
                data-testid="dialog-title"
                variant="body2"
                sx={{
                  pl: 1,
                  fontWeight: 700,
                  fontSize: 20,
                  lineHeight: 1.4,
                  ...titleTextSx
                }}
              >
                {title}
              </Typography>
              {Boolean(subtitle) && (
                <Typography
                  data-testid="dialog-subtitle"
                  sx={{
                    pl: 1,
                    mt: 0.25,
                    fontSize: 12,
                    color: grey[600],
                    ...subtitleTextSx
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Stack>
          )}
          <Stack direction="row" columnGap={2}>
            {extraTitleButton}
            <IconButton
              onClick={handleCloseClick}
              onTouchStart={handleCloseClick}
              sx={{ marginLeft: "auto" }}
              data-testid="close-dialog"
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent data-testid="dialog-content" {...dialogContentProps}>
        {children}
      </DialogContent>
      {actions && (
        <DialogActions data-testid="dialog-actions" sx={dialogActionsSx}>
          {actions}
        </DialogActions>
      )}
    </MuiDialog>
  );
}

// Memoize the Dialog component to prevent unnecessary re-renders when props haven't changed
export default memo(Dialog);
